/**
 * Small pill on the round screen that shows the GPS signal quality at a glance.
 *  - GREEN  "LIVE"     → using GPS source AND accuracy ≤ 12m AND fix < 6s old
 *  - AMBER  "WEAK"     → using GPS but accuracy poor OR stale fix
 *  - GREY   "PAR YDS"  → fallback to course-default (no GPS lock)
 *  - RED    "GPS OFF"  → permission denied / hardware error
 */
export default function GpsSignalPill({ source, accuracy, lastFixAt, hasError }) {
  const ageSec = lastFixAt ? Math.round((Date.now() - lastFixAt) / 1000) : null;
  let tone, label, sub;
  if (hasError) {
    tone = "red"; label = "GPS off"; sub = "permission?";
  } else if (source === "gps") {
    const good = accuracy != null && accuracy <= 12 && ageSec != null && ageSec < 6;
    tone = good ? "green" : "amber";
    label = good ? "Live" : "Weak";
    sub = accuracy != null ? `±${Math.round(accuracy)}m` : (ageSec != null ? `${ageSec}s ago` : "");
  } else {
    tone = "grey"; label = "Par yds"; sub = "no GPS lock";
  }
  const toneStyles = {
    green: { bg: "rgba(57,150,90,0.18)", dot: "#39965A", text: "#D4F4DD" },
    amber: { bg: "rgba(228,160,68,0.2)", dot: "#E4A044", text: "#FFE7B8" },
    grey:  { bg: "rgba(242,238,231,0.1)", dot: "#A7B0AC", text: "rgba(242,238,231,0.8)" },
    red:   { bg: "rgba(196,84,60,0.2)", dot: "#C4543C", text: "#FFD8CE" },
  }[tone];
  return (
    <span
      data-testid="gps-signal-pill"
      data-tone={tone}
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold font-heading uppercase tracking-wider whitespace-nowrap"
      style={{ background: toneStyles.bg, color: toneStyles.text }}
    >
      <span
        className={tone === "green" ? "pulse-dot" : ""}
        style={{ width: 6, height: 6, borderRadius: 999, background: toneStyles.dot, display: "inline-block" }}
      />
      {label}
      {sub && <span className="opacity-70 normal-case tracking-normal font-mono-num text-[10px]">{sub}</span>}
    </span>
  );
}
